import React, { createContext, useContext, useMemo, useState } from "react";
import { useLoaderData, useLocation } from "react-router";

import { selectSetting } from "@/app/src/features/global/globalSlice";
import { useAppSelector } from "@/app/src/hooks/reduxHooks";

import { IOrganization, ITeam } from "@src/types/api";
import { AsyncStatuses } from "@src/types/shared";

import {
    OrganizationsContextProps,
    OrganizationsContextValues,
} from "@LeagueManagement/Organizations/types/contexts";

const OrganizationsContext = createContext<OrganizationsContextValues | null>(
    null,
);

export const useOrganizationsContext = () => {
    const context = useContext(OrganizationsContext);
    if (!context) {
        throw new Error(
            "useOrganizationsContext must be used within a OrganizationsProvider",
        );
    }
    return context;
};

export const OrganizationsProvider: React.FC<
    OrganizationsContextProps
> = props => {
    const {
        children,
        organizations: loadedOrganizations = [],
        teams = [],
    } = props;

    const [fetchStatus, setFetchStatus] = useState<AsyncStatuses>("init");
    const setting = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );
    const currentYear = setting?.setting ?? new Date().getFullYear();
    const [organizations, setOrganizations] =
        useState<IOrganization[]>(loadedOrganizations);

    organizations?.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    const teamsList = useMemo(
        () =>
            teams
                ?.sort((a, b) => {
                    const nameCompare = a.name.localeCompare(b.name);

                    if (nameCompare !== 0) {
                        return nameCompare;
                    } else {
                        const ageLevelA = a.ageLevels.find(
                            a => a.year === parseInt(currentYear),
                        )?.ageLevel;

                        const ageLevelB = b.ageLevels.find(
                            a => a.year === parseInt(currentYear),
                        )?.ageLevel;

                        const ageComparison =
                            ageLevelA && ageLevelB
                                ? parseInt(ageLevelA) - parseInt(ageLevelB)
                                : ageLevelA
                                  ? -1
                                  : ageLevelB
                                    ? 1
                                    : 0;

                        if (ageComparison === 0) {
                            return a.name.localeCompare(b.name);
                        }

                        return ageComparison;
                    }
                })
                .map(t => {
                    const ageLevel = t.ageLevels.find(
                        a => a.year === parseInt(currentYear),
                    )?.ageLevel;

                    return {
                        value: t.id,
                        label: `${t.name}${ageLevel ? ` (${ageLevel})` : ""} - (${
                            t.coach.firstName
                        } ${t.coach.lastName} - ${t.coach.email})`,
                    };
                }) ?? [],
        [currentYear, teams],
    );

    const pendingApproval = useMemo(
        () => organizations?.filter(o => o.approved === 0),
        [organizations],
    );

    const contextValue = useMemo(
        () => ({
            fetchStatus,
            setFetchStatus,
            organizations,
            setOrganizations,
            pendingApproval,
            teamsList,
        }),
        [fetchStatus, organizations, teamsList],
    );

    return (
        <OrganizationsContext.Provider value={contextValue}>
            {children}
        </OrganizationsContext.Provider>
    );
};
